import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import toast from "react-hot-toast";
import { HiOutlineArrowLeft } from "react-icons/hi";
import { FiEdit2, FiRefreshCw } from "react-icons/fi";
import {
  getPurchaseOrderById,
  getPurchaseOrderItems,
  refreshPurchaseOrder,
  updatePurchaseOrder,
} from "../../Services/PurchaseOrderService";
import { getSites } from "../../Services/siteService";
import {
  Loader,
  ExpandableInfoPanel,
  Modal,
  Button,
  SearchableDropdown,
} from "../../Components/Common";
import {
  MESSAGES,
  PO_INFO_LABELS,
  PO_STATUS_STYLES,
  PO_RECEIVED_STATUS_STYLES,
  PO_BILLED_STATUS_STYLES,
  formatStatusLabel,
} from "./constants";
import { PURCHASE_ORDER_LIST } from "../../routes";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import { formatDateDDMMYYYY } from "../../Utils";
import { UserContext } from "../../Context";
import { ROLES } from "../../Constants/Strings";

const ITEMS_PAGINATION = { count: 0, offset: 0, limit: 10 };

const formatDateLong = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const StatusBadge = ({ value, styles }) => (
  <span
    className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
      styles[value] || "bg-gray-100 text-gray-700"
    }`}
  >
    {formatStatusLabel(value)}
  </span>
);

const PODetails = () => {
  const { poId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const isAccounts = user?.role?.toLowerCase() === ROLES.ACCOUNTS_ROLE;

  const [po, setPo] = useState(null);
  const [loadingPo, setLoadingPo] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [items, setItems] = useState([]);
  const [itemsPagination, setItemsPagination] = useState(ITEMS_PAGINATION);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsPaginationLoading, setItemsPaginationLoading] = useState(false);

  const [editSiteOpen, setEditSiteOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [savingSite, setSavingSite] = useState(false);

  const loadPo = useCallback(async () => {
    setLoadingPo(true);
    try {
      const response = await getPurchaseOrderById(poId);
      if (response.status === 200) {
        setPo(response.data);
      } else {
        toast.error(extractErrorMessage(response) || MESSAGES.DETAIL_LOAD_ERROR);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || MESSAGES.DETAIL_LOAD_ERROR);
    } finally {
      setLoadingPo(false);
    }
  }, [poId]);

  const loadItems = async (finalParams, isInitial = false) => {
    if (isInitial) {
      setItemsLoading(true);
    } else {
      setItemsPaginationLoading(true);
    }

    try {
      const response = await getPurchaseOrderItems(poId, finalParams);
      if (response.status === 200) {
        const newItems = response.data.results || [];
        setItems((prev) => (isInitial ? newItems : [...prev, ...newItems]));
        setItemsPagination((prev) => ({
          ...prev,
          count: response.data.count,
          offset: prev.offset + prev.limit,
        }));
      } else {
        toast.error(extractErrorMessage(response) || MESSAGES.ITEMS_LOAD_ERROR);
        if (isInitial) setItems([]);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || MESSAGES.ITEMS_LOAD_ERROR);
      if (isInitial) setItems([]);
    } finally {
      if (isInitial) {
        setItemsLoading(false);
      } else {
        setItemsPaginationLoading(false);
      }
    }
  };

  const loadMoreItems = () => {
    if (itemsPagination.offset >= itemsPagination.count) return;
    loadItems({ offset: itemsPagination.offset, limit: itemsPagination.limit });
  };

  const refreshItemsList = () => {
    setItemsPagination(ITEMS_PAGINATION);
    loadItems({ offset: 0, limit: ITEMS_PAGINATION.limit }, true);
  };

  useEffect(() => {
    loadPo();
    loadItems({ offset: 0, limit: ITEMS_PAGINATION.limit }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poId]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await refreshPurchaseOrder(poId);
      if (response.status === 200) {
        await loadPo();
        refreshItemsList();
        toast.success(MESSAGES.REFRESH_SUCCESS);
      } else {
        toast.error(extractErrorMessage(response) || MESSAGES.REFRESH_ERROR);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || MESSAGES.REFRESH_ERROR);
    } finally {
      setRefreshing(false);
    }
  };

  // Fetches sites for the picker (fetchFunction contract for SearchableDropdown)
  const searchSites = async ({ search = "" } = {}) => {
    try {
      const response = await getSites({ search, is_active: true, limit: 20 });
      if (response.status === 200) {
        return response.data?.results || [];
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || MESSAGES.SITE_SEARCH_ERROR);
    }
    return [];
  };

  const openEditSite = () => {
    setSelectedSite(
      po.site ? { id: po.site, name: po.site_name || "" } : null
    );
    setEditSiteOpen(true);
  };

  const closeEditSite = () => {
    setEditSiteOpen(false);
    setSelectedSite(null);
    setSavingSite(false);
  };

  const handleSaveSite = async () => {
    setSavingSite(true);
    try {
      const response = await updatePurchaseOrder(poId, {
        site: selectedSite?.id || null,
      });
      if (response.status === 200) {
        setPo((prev) => ({ ...prev, ...response.data }));
        toast.success(MESSAGES.SITE_UPDATE_SUCCESS);
        closeEditSite();
      } else {
        toast.error(extractErrorMessage(response) || MESSAGES.SITE_UPDATE_ERROR);
        setSavingSite(false);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || MESSAGES.SITE_UPDATE_ERROR);
      setSavingSite(false);
    }
  };

  if (loadingPo || !po) {
    return (
      <div className="flex justify-center items-center h-[70vh]">
        <Loader size={4} />
      </div>
    );
  }

  return (
    <div className="px-4 my-4">
      {/* Mobile header: title row, then status row */}
      <div className="md:hidden">
        <div className="flex items-center gap-3 mb-1">
          <button
            onClick={() => navigate(PURCHASE_ORDER_LIST)}
            className="text-primary text-2xl"
          >
            <HiOutlineArrowLeft />
          </button>
          <div className="text-xl text-primary font-semibold flex-1 truncate">
            {po.zoho_po_number}
          </div>
        </div>
        <div className="flex items-center flex-wrap gap-2 mb-4 px-1">
          <StatusBadge value={po.status} styles={PO_STATUS_STYLES} />
          <StatusBadge value={po.received_status} styles={PO_RECEIVED_STATUS_STYLES} />
          <StatusBadge value={po.billed_status} styles={PO_BILLED_STATUS_STYLES} />
        </div>
      </div>

      {/* Desktop header: back button, title, and status all in one row */}
      <div className="hidden md:flex md:items-center md:gap-3 md:mb-4">
        <button
          onClick={() => navigate(PURCHASE_ORDER_LIST)}
          className="text-primary text-2xl"
        >
          <HiOutlineArrowLeft />
        </button>
        <div className="text-2xl text-primary font-semibold flex-1 truncate">
          {po.zoho_po_number}
        </div>
        <StatusBadge value={po.status} styles={PO_STATUS_STYLES} />
        <StatusBadge value={po.received_status} styles={PO_RECEIVED_STATUS_STYLES} />
        <StatusBadge value={po.billed_status} styles={PO_BILLED_STATUS_STYLES} />
      </div>

      {/* PO Info */}
      <ExpandableInfoPanel
        title={po.vendor_name}
        subtitle={`${po.site_name || po.site || "-"} · PO ${formatDateLong(po.po_date)}`}
        fields={[
          { label: PO_INFO_LABELS.zoho_po_number, value: po.zoho_po_number },
          { label: PO_INFO_LABELS.vendor_name, value: po.vendor_name },
          { label: PO_INFO_LABELS.po_date, value: formatDateDDMMYYYY(po.po_date) },
          {
            label: PO_INFO_LABELS.delivery_date,
            value: formatDateDDMMYYYY(po.delivery_date),
          },
          {
            label: PO_INFO_LABELS.expected_delivery_date,
            value: formatDateDDMMYYYY(po.expected_delivery_date),
          },
          { label: PO_INFO_LABELS.site, value: po.site_name || po.site },
        ]}
        mobileFields={[
          { label: "Vendor", value: po.vendor_name },
          { label: "Site", value: po.site_name || po.site },
          {
            label: "Expected Delivery",
            value: formatDateDDMMYYYY(po.expected_delivery_date),
          },
        ]}
        footer={`PO dated ${formatDateLong(po.po_date)}${
          po.delivery_date ? ` · Delivered ${formatDateLong(po.delivery_date)}` : ""
        }`}
        endAction={
          isAccounts && (
            <button
              type="button"
              className="flex items-center gap-1 text-primary text-sm"
              onClick={openEditSite}
              title="Edit Site"
            >
              <FiEdit2 size={14} />
              Edit Site
            </button>
          )
        }
      />

      {isAccounts && (
        <div className="md:hidden border rounded-lg px-4 py-3 mb-4 bg-gray-50 flex items-center justify-between gap-3">
          <div className="text-sm min-w-0 truncate">
            <span className="text-gray-500">Site: </span>
            <span className="font-medium text-gray-700">
              {po.site_name || "Not assigned"}
            </span>
          </div>
          <button
            type="button"
            className="text-primary text-sm flex items-center gap-1 shrink-0"
            onClick={openEditSite}
          >
            <FiEdit2 size={14} />
            Edit
          </button>
        </div>
      )}

      {/* Items */}
      <div className="border rounded-md p-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="font-semibold text-secondary">PO Items</div>
            <span className="text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
              {itemsPagination.count}
            </span>
          </div>
          <button
            type="button"
            className="text-primary text-sm flex items-center gap-1 disabled:text-gray-400"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <FiRefreshCw className={refreshing ? "animate-spin" : ""} />
            Refresh from Zoho
          </button>
        </div>

        {itemsLoading ? (
          <div className="flex justify-center py-6">
            <Loader size={3} />
          </div>
        ) : items.length === 0 ? (
          <div className="text-sm text-gray-500 text-center py-4">
            No items found
          </div>
        ) : (
          <div id="scrollablePoDetailItemsDiv" className="max-h-[60vh] overflow-y-auto">
            <InfiniteScroll
              dataLength={items.length}
              next={loadMoreItems}
              hasMore={itemsPagination.offset < itemsPagination.count}
              scrollableTarget="scrollablePoDetailItemsDiv"
            >
              <div className="space-y-2">
                {items.map((item) => (
                  <div
                    key={item.line_item_id}
                    className={`border rounded-md p-2 flex justify-between items-center gap-2 ${
                      item.is_deleted ? "bg-gray-50" : "bg-white"
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div
                          className={`text-sm font-medium truncate ${
                            item.is_deleted
                              ? "text-gray-400 line-through"
                              : "text-secondary"
                          }`}
                        >
                          {item.name}
                        </div>
                        {item.is_deleted && (
                          <span className="shrink-0 inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium bg-red-100 text-red-700">
                            Deleted
                          </span>
                        )}
                      </div>
                      {item.sku && (
                        <div className="text-xs text-gray-500 truncate">
                          SKU: {item.sku}
                        </div>
                      )}
                      <div className="text-xs mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                        <span className="text-gray-500">
                          Required:{" "}
                          <span className="font-medium text-gray-700">
                            {item.quantity} {item.unit}
                          </span>
                        </span>
                        <span className="text-gray-500">
                          Received:{" "}
                          <span className="font-medium text-gray-700">
                            {item.received_quantity} {item.unit}
                          </span>
                        </span>
                        <span
                          className={
                            Number(item.remaining_quantity) > 0
                              ? "text-gray-500"
                              : "text-green-600"
                          }
                        >
                          Remaining:{" "}
                          <span className="font-medium">
                            {item.remaining_quantity} {item.unit}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </InfiniteScroll>
            {itemsPaginationLoading && (
              <div className="flex justify-center py-3">
                <Loader size={3} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Site Modal (Accounts only) */}
      <Modal
        isOpen={editSiteOpen}
        title="Edit Site"
        footer={
          <>
            <button
              className="px-4 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              onClick={closeEditSite}
              disabled={savingSite}
            >
              Cancel
            </button>
            <Button
              className="w-auto py-2 px-6"
              onClick={handleSaveSite}
              disabled={savingSite}
            >
              {savingSite ? "Saving..." : "Save"}
            </Button>
          </>
        }
      >
        <div className="mx-auto max-w-xs">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Site
          </label>
          <SearchableDropdown
            fetchFunction={searchSites}
            onSelect={(site) => setSelectedSite(site)}
            value={selectedSite}
            valueKey="id"
            labelKey="name"
            placeholder="Select Site"
            search_box_placeholder="Search sites..."
          />
        </div>
      </Modal>
    </div>
  );
};

export default PODetails;
